import { formatAuditOccurredAtParts } from '@/utils/auditLogDisplay';
import { formatPhoneDisplay } from '@/utils/participantDetailDisplay';

export const SESSION_WHATSAPP_TYPE_LABEL: Record<
	SessionWhatsApp.SessionWhatsAppNotificationType,
	string
> = {
	SESSION_SCHEDULED: 'Agendamento confirmado',
	SESSION_UPCOMING: 'Lembrete (30 min antes)',
};

export const SESSION_WHATSAPP_STATUS_LABEL: Record<
	SessionWhatsApp.SessionWhatsAppNotificationStatus,
	string
> = {
	QUEUED: 'Na fila',
	SENT: 'Enviada',
	DELIVERED: 'Entregue',
	READ: 'Lida',
	FAILED: 'Falhou',
	CANCELLED: 'Cancelada',
};

export const SESSION_WHATSAPP_STATUS_STYLES: Record<
	SessionWhatsApp.SessionWhatsAppNotificationStatus,
	string
> = {
	QUEUED: 'bg-amber-50 text-amber-800 border-amber-200',
	SENT: 'bg-sky-50 text-sky-800 border-sky-200',
	DELIVERED: 'bg-emerald-50 text-emerald-800 border-emerald-200',
	READ: 'bg-emerald-100 text-emerald-900 border-emerald-300',
	FAILED: 'bg-rose-50 text-rose-800 border-rose-200',
	CANCELLED: 'bg-zinc-100 text-zinc-600 border-zinc-200',
};

export const SESSION_WHATSAPP_STATUSES: SessionWhatsApp.SessionWhatsAppNotificationStatus[] =
	['QUEUED', 'SENT', 'DELIVERED', 'READ', 'FAILED', 'CANCELLED'];

export const SESSION_WHATSAPP_TYPES: SessionWhatsApp.SessionWhatsAppNotificationType[] =
	['SESSION_SCHEDULED', 'SESSION_UPCOMING'];

export const isValidWhatsAppPhone = (
	phone: string | null | undefined,
): boolean => /^\d{11}$/.test(phone ?? '');

export const formatSessionWhatsAppScheduledAt = (iso: string): string => {
	const { date, time } = formatAuditOccurredAtParts(iso);
	if (!date) return iso;
	return `${date} ${time.slice(0, 5)}`;
};

export const formatSessionWhatsAppPhone = (phone: string): string =>
	formatPhoneDisplay(phone) ?? phone;

export const formatSessionWhatsAppMessagePreview = (
	body: string,
	maxLength = 60,
): string => {
	const normalized = body.replace(/\s+/g, ' ').trim();
	if (normalized.length <= maxLength) return normalized;
	return `${normalized.slice(0, maxLength)}…`;
};

export type SessionWhatsAppReceiptLevel =
	| 'pending'
	| 'sent'
	| 'delivered'
	| 'read'
	| 'failed'
	| 'cancelled';

export const formatSessionWhatsAppMessageTime = (iso: string): string => {
	const { time } = formatAuditOccurredAtParts(iso);
	return time.slice(0, 5);
};

export const getSessionWhatsAppReceiptLevel = (
	notification: SessionWhatsApp.SessionWhatsAppNotification,
): SessionWhatsAppReceiptLevel => {
	if (notification.status === 'CANCELLED' || notification.cancelled_at) {
		return 'cancelled';
	}
	if (notification.status === 'FAILED') {
		return 'failed';
	}
	if (!notification.sent_at) {
		return 'pending';
	}
	if (notification.read_at || notification.status === 'READ') {
		return 'read';
	}
	if (notification.delivered_at || notification.status === 'DELIVERED') {
		return 'delivered';
	}
	return 'sent';
};

export const getSessionWhatsAppMessageDisplayTime = (
	notification: SessionWhatsApp.SessionWhatsAppNotification,
): string | null => {
	if (notification.sent_at) {
		return formatSessionWhatsAppMessageTime(notification.sent_at);
	}
	return null;
};

export type SessionWhatsAppTimelineEntry = {
	key: string;
	label: string;
	at: string;
};

export const getSessionWhatsAppTimelineEntries = (
	notification: SessionWhatsApp.SessionWhatsAppNotification,
): SessionWhatsAppTimelineEntry[] => {
	const entries: SessionWhatsAppTimelineEntry[] = [];

	if (notification.sent_at) {
		entries.push({
			key: 'sent',
			label: 'Enviada',
			at: notification.sent_at,
		});
	}
	if (notification.delivered_at) {
		entries.push({
			key: 'delivered',
			label: 'Entregue',
			at: notification.delivered_at,
		});
	}
	if (notification.read_at) {
		entries.push({
			key: 'read',
			label: 'Lida',
			at: notification.read_at,
		});
	}
	if (notification.cancelled_at) {
		entries.push({
			key: 'cancelled',
			label: 'Cancelada',
			at: notification.cancelled_at,
		});
	}

	return entries;
};

export type SessionWhatsAppRelevantTimestamp = {
	label: string;
	at: string;
};

export const getSessionWhatsAppRelevantTimestamp = (
	notification: SessionWhatsApp.SessionWhatsAppNotification,
): SessionWhatsAppRelevantTimestamp => {
	if (notification.cancelled_at) {
		return { label: 'Cancelada', at: notification.cancelled_at };
	}
	if (notification.read_at) {
		return { label: 'Lida', at: notification.read_at };
	}
	if (notification.delivered_at) {
		return { label: 'Entregue', at: notification.delivered_at };
	}
	if (notification.sent_at) {
		return { label: 'Enviada', at: notification.sent_at };
	}
	if (notification.status === 'FAILED') {
		return { label: 'Falhou', at: notification.updated_at };
	}
	return { label: 'Agendada', at: notification.scheduled_for };
};

export const formatSessionWhatsAppRelevantTimestamp = (
	notification: SessionWhatsApp.SessionWhatsAppNotification,
): string => {
	const { label, at } = getSessionWhatsAppRelevantTimestamp(notification);
	return `${label} · ${formatSessionWhatsAppScheduledAt(at)}`;
};

export const buildDefaultSessionWhatsAppMessage = (
	type: SessionWhatsApp.SessionWhatsAppNotificationType,
	params: {
		patientName: string;
		sessionDateIso?: string;
		professionalName?: string;
	},
): string => {
	const firstName =
		params.patientName.trim().split(/\s+/).filter(Boolean)[0] || 'paciente';
	const scheduledAt = params.sessionDateIso
		? formatSessionWhatsAppScheduledAt(params.sessionDateIso)
		: 'data e horário a confirmar';
	const professional = params.professionalName?.trim() || 'seu profissional';

	if (type === 'SESSION_UPCOMING') {
		return `Olá, ${firstName}! Tudo bem?\n\nLembrete: sua consulta com ${professional} começa em breve (${scheduledAt}).\n\nPlumaa`;
	}

	return `Olá, ${firstName}! Tudo bem?\n\nSua consulta com ${professional} foi agendada para ${scheduledAt}.\n\nQualquer dúvida, estamos à disposição.\n\nPlumaa`;
};
